/** Planets tab: quick sell, the selected planet (upgrades, colony, prospector), then the planet list. */

import { PLANETS, PLANET_STATS, PLANET_STAT_LABEL, type PlanetStat } from '../../content/planets';
import { resource } from '../../content/resources';
import { COLONY_HAUL_PER_LEVEL, COLONY_MAX_LEVEL, COLONY_MINE_PER_LEVEL } from '../../content/colonies';
import { PROSPECTOR_RARITY_COLOR, PROSPECTOR_ROLE_LABEL, prospectorBonus, prospectorDef } from '../../content/prospectors';
import {
  affordableLevels, bottleneck, bulkUpgradeCost, cargoCap, haulRate, legTime, mineRate, planetIncome,
  sellPrice, unlockCost,
} from '../../sim/economy';
import { isVisible } from '../../sim/actions';
import { hasFeature, hasProject, telescopes, visiblePlanets } from '../../sim/projects';
import { missionAvailable, missionSeconds } from '../../sim/rovers';
import { ROVER_MISSIONS } from '../../content/rovers';
import { canColonize, nextColonyCost } from '../../sim/planets';
import { roomMult } from '../../sim/rooms';
import type { GameState } from '../../sim/state';
import { cur, esc, fmtFine, fmtNum, fmtTime, price } from '../format';
import { btn, ingredients, type View } from '../view';
import { nextGoal } from '../goals';
import { icon } from '../icons';

/** Evaluates `fn` as if the stat were at `level`, then restores it. */
function atLevel<T>(state: GameState, index: number, stat: PlanetStat, level: number, fn: () => T): T {
  const levels = state.run.planets[index]!.levels;
  const was = levels[stat];
  levels[stat] = level;
  try { return fn(); } finally { levels[stat] = was; }
}

function statValue(v: View, index: number, stat: PlanetStat): string {
  const { state, stats } = v;
  switch (stat) {
    case 'mine': return `${fmtFine(mineRate(state, stats, index))}/s`;
    case 'ship': return `${fmtFine(legTime(state, stats, index))}s trip`;
    case 'cargo': return fmtNum(cargoCap(state, stats, index));
  }
}

function upgradeRow(v: View, index: number, stat: PlanetStat, isNeck: boolean): string {
  const { state, stats, ui } = v;
  const level = state.run.planets[index]!.levels[stat];
  const gc = state.run.gc;
  const count = ui.buyMode === 'max' ? Math.max(1, affordableLevels(state, stats, index, stat, level, gc)) : ui.buyMode;
  const cost = bulkUpgradeCost(state, stats, index, stat, level, count);
  const next = atLevel(state, index, stat, level + count, () => statValue(v, index, stat));
  const accent = stat === 'mine' ? 'var(--gold)' : 'var(--cyan)';
  return `<div class="vm-row${isNeck ? ' selected' : ''}" style="--accent:${accent}" data-key="up-${stat}">
    <div class="vm-row-main">
      <div class="vm-row-name">${PLANET_STAT_LABEL[stat]} <span class="vm-lv cyan">Lv ${level}</span>${isNeck ? ' <span class="vm-tag contested">Bottleneck</span>' : ''}</div>
      <div class="vm-row-effect"><b>${statValue(v, index, stat)}</b> → ${next}</div>
    </div>
    ${btn(`+${count}<br>${price('gc', cost)}`, 'upgrade', { cls: 'buy', disabled: gc < cost, data: { planet: index, stat, count: ui.buyMode === 'max' ? 'max' : count } })}
  </div>`;
}

function oreLine(index: number): string {
  return `<div class="vm-chipline">${PLANETS[index]!.ores.map((o) => {
    const def = resource(o.id);
    return `<span><span class="vm-dot" style="background:${def.color}"></span>${def.name} ${Math.round(o.share * 100)}%</span>`;
  }).join('')}</div>`;
}

function colonyRow(v: View, index: number): string {
  const { state } = v;
  if (!hasFeature(state, 'colonization')) return '';
  const level = state.run.planets[index]!.colony;
  const relations = roomMult(state, 'colonyBonus');
  const now = `Mining +${Math.round(COLONY_MINE_PER_LEVEL * level * relations * 100)}% · haul +${Math.round(COLONY_HAUL_PER_LEVEL * level * relations * 100)}%`;
  if (level >= COLONY_MAX_LEVEL) {
    return `<div class="vm-row" style="--accent:var(--green)" data-key="colony"><div class="vm-row-main">
      <div class="vm-row-name">Colony <span class="vm-lv gold">Max</span></div><div class="vm-row-effect"><b>${now}</b></div></div></div>`;
  }
  const cost = nextColonyCost(state, index);
  return `<div class="vm-row" style="--accent:var(--green)" data-key="colony">
    <div class="vm-row-main">
      <div class="vm-row-name">Colony <span class="vm-lv cyan">Lv ${level}/${COLONY_MAX_LEVEL}</span></div>
      <div class="vm-row-effect">${level > 0 ? `<b>${now}</b><br>` : 'Settle it with crafted items for a lasting boost to this planet.<br>'}Next: ${ingredients(state, cost)}</div>
    </div>
    ${btn('Colonize', 'colonize', { cls: 'green', disabled: !canColonize(state, index), data: { planet: index } })}
  </div>`;
}

/** IPM's rover: send it to scan this planet; one mission at a time. */
function roverRow(v: View, index: number): string {
  const { state } = v;
  if (!hasProject(state, 'rover') && !state.rover) return '';
  const r = state.rover;
  if (r) {
    const left = Math.max(0, r.endsAt - state.clock);
    const here = r.planet === index;
    const total = missionSeconds(state, r.mission);
    const pct = Math.min(100, (100 * (total - left)) / Math.max(1, total));
    return `<div class="vm-row${here ? ' selected' : ''}" style="--accent:var(--cyan)" data-key="rover"><div class="vm-row-main">
      <div class="vm-row-name">Rover <span class="vm-lv cyan">${fmtTime(left)}</span></div>
      <div class="vm-row-effect">${here ? 'Scanning this planet' : `Scanning ${esc(PLANETS[r.planet]!.name)}`} · ${ROVER_MISSIONS[r.mission]!.hours}h mission pays ${price('sc', ROVER_MISSIONS[r.mission]!.sc)}${here && hasProject(state, 'rover_resupply') ? ' · resupplied ×2.5' : ''}</div>
      <div class="vm-bar"><i style="width:${pct.toFixed(1)}%"></i></div></div></div>`;
  }
  const buttons = ROVER_MISSIONS.map((m, k) => missionAvailable(state, k)
    ? btn(`${m.hours}h<br>${price('sc', m.sc)}`, 'rover', { cls: 'buy ghost', data: { planet: index, mission: k } })
    : '').join('');
  return `<div class="vm-row" style="--accent:var(--cyan)" data-key="rover"><div class="vm-row-main">
      <div class="vm-row-name">Rover <span class="vm-lv">idle</span></div>
      <div class="vm-row-effect">Scan this planet for Stellar Cores and a chance of a Prospector.${hasProject(state, 'rover_resupply') ? ' The planet runs ×2.5 while scanned.' : ''}</div>
      <div class="vm-btns" style="justify-content:flex-start;margin-top:6px">${buttons}</div></div></div>`;
}

function prospectorRow(v: View, index: number): string {
  const { state } = v;
  if (!hasFeature(state, 'management')) return '';
  const pid = state.assignments[String(index)];
  const owned = Object.keys(state.prospectors).length;
  if (!pid) {
    return `<div class="vm-row" style="--accent:var(--line)" data-key="prospector"><div class="vm-row-main">
      <div class="vm-row-name vm-faint">No prospector</div>
      <div class="vm-row-effect">${owned ? 'Assign one to multiply this planet.' : 'Recruit Prospectors at the Station with ★ SC.'}</div></div>
      ${owned ? btn('Assign', 'openAssign', { cls: 'ghost', data: { planet: index } }) : btn('Crew', 'station', { cls: 'ghost', data: { tab: 'crew' } })}</div>`;
  }
  const def = prospectorDef(pid);
  const level = state.prospectors[pid] ?? 1;
  const bonus = prospectorBonus(def, level) * roomMult(state, 'prospector');
  return `<div class="vm-row" style="--accent:${PROSPECTOR_RARITY_COLOR[def.rarity]}" data-key="prospector">
    <div class="vm-row-main">
      <div class="vm-row-name" style="color:${PROSPECTOR_RARITY_COLOR[def.rarity]}">${esc(def.name)} <span class="vm-lv">Lv ${level}</span></div>
      <div class="vm-row-effect">${PROSPECTOR_ROLE_LABEL[def.role]} · <b>×${fmtFine(1 + bonus)} ${PLANET_STAT_LABEL[def.role]}</b></div>
    </div>
    ${btn('Change', 'openAssign', { cls: 'ghost', data: { planet: index } })}
  </div>`;
}

function selectedCard(v: View, index: number): string {
  const { state, stats, ui } = v;
  const def = PLANETS[index]!;
  const p = state.run.planets[index]!;
  const neck = bottleneck(state, stats, index);
  const modes = ([1, 10, 'max'] as const).map((m) =>
    btn(m === 'max' ? 'MAX' : `×${m}`, 'buyMode', { cls: ui.buyMode === m ? 'on' : 'ghost', data: { mode: m } })).join('');
  return `<section class="vm-section" data-key="sel-${index}">
    <div class="vm-section-head cyan">${def.name} <span class="aside">Planet ${index + 1}${p.colony ? ` · Colony ${p.colony}` : ''}</span></div>
    ${oreLine(index)}
    <div class="vm-stats">
      <div><span>Mining</span><b>${fmtFine(mineRate(state, stats, index))}/s</b></div>
      <div><span>Hauling</span><b>${fmtFine(haulRate(state, stats, index))}/s</b></div>
      <div><span>Income</span><b>${cur('gc')} ${fmtFine(planetIncome(state, stats, index))}/s</b></div>
      <div><span>Stockpile</span><b>${fmtNum(p.stock)}</b></div>
    </div>
    <div class="vm-seg" style="margin-bottom:8px">${modes}</div>
    <div class="vm-rows">
      ${PLANET_STATS.map((s) => upgradeRow(v, index, s, neck === 'mine' ? s === 'mine' : s !== 'mine')).join('')}
      ${colonyRow(v, index)}
      ${prospectorRow(v, index)}
      ${roverRow(v, index)}
    </div>
  </section>`;
}

function unlockCard(v: View, index: number): string {
  const def = PLANETS[index]!;
  const visible = isVisible(v.state, index);
  const cost = unlockCost(v.state, v.stats, index);
  return `<section class="vm-section" data-key="unlock-${index}">
    <div class="vm-section-head gold">${def.name} <span class="aside">Planet ${index + 1} · locked</span></div>
    ${oreLine(index)}
    <p class="vm-note" style="margin-top:8px">${visible ? 'Buy it to start a new mining route.' : `Out of telescope range. Research <b>Telescope ${telescopes(v.state) + 1}</b> in Projects to reveal it.`}</p>
    ${btn(`Buy · ${price('gc', cost)}`, 'buyPlanet', { cls: 'wide gold', disabled: !visible || v.state.run.gc < cost, data: { planet: index } })}
  </section>`;
}

function listRow(v: View, index: number): string {
  const { state, stats, ui } = v;
  const def = PLANETS[index]!;
  const p = state.run.planets[index]!;
  const selected = ui.selected === index ? ' selected' : '';
  if (!p.owned) {
    const cost = unlockCost(state, stats, index);
    // Unowned: a neutral edge, lit green only when you can afford it.
    const accent = state.run.gc >= cost ? 'var(--green)' : 'var(--line)';
    return `<div class="vm-row clickable${selected}" style="--accent:${accent}" data-act="select" data-planet="${index}" data-key="pl-${index}">
      <div class="vm-row-main">
        <div class="vm-row-name"><span class="vm-locked-planet">${icon('lock')}</span>${def.name}</div>
        <div class="vm-row-effect">${def.ores.map((o) => resource(o.id).name).join(' · ')}</div>
      </div>
      <span class="vm-lv ${state.run.gc >= cost ? 'green' : ''}">${price('gc', cost)}</span>
    </div>`;
  }
  const lv = p.levels;
  return `<div class="vm-row clickable${selected}" style="--accent:${resource(def.ores[def.ores.length - 1]!.id).color}" data-act="select" data-planet="${index}" data-key="pl-${index}">
    <div class="vm-row-main">
      <div class="vm-row-name">${def.name}${p.colony ? ` <span class="vm-lv green">⚑${p.colony}</span>` : ''}</div>
      <div class="vm-row-effect">Mine ${lv.mine} · Ship ${lv.ship} · Cargo ${lv.cargo}</div>
    </div>
    <span class="vm-lv gold">${cur('gc')} ${fmtFine(planetIncome(state, stats, index))}/s</span>
  </div>`;
}

/** Sell-everything strip: ore piles up in the Hold, and selling it is the main action. Hidden until there is ore. */
function quickSell(v: View): string {
  const { state, stats } = v;
  const ores = Object.entries(state.run.hold).filter(([id, q]) => q > 0 && resource(id).kind === 'ore');
  const total = ores.reduce((sum, [id, q]) => sum + q * sellPrice(state, stats, id), 0);
  if (total < 1 && state.run.lifetimeGc === 0) return '';
  return `<div class="vm-quick" data-key="quick">
    ${btn(`Sell ores · ${price('gc', total)}`, 'sellKind', { cls: 'gold', disabled: total < 1, data: { kind: 'ore' } })}
  </div>`;
}

export function renderPlanets(v: View): string {
  const { state, ui } = v;
  const index = ui.selected;
  const card = state.run.planets[index]!.owned ? selectedCard(v, index) : unlockCard(v, index);
  const visible = visiblePlanets(state);
  const shown = PLANETS.filter((_, i) => i < visible);
  const owned = state.run.planets.filter((p) => p.owned).length;
  const goal = nextGoal(state);
  const hint = goal
    ? `<div class="vm-tip vm-goal" data-key="goal"><span><b class="vm-goal-tag">Next</b> ${goal.text}</span>${goal.go && !(goal.go[1] === 'select' && goal.go[2].planet === index) ? btn(goal.go[0], goal.go[1], { cls: 'gold', data: { ...goal.go[2] } }) : ''}</div>`
    : '';
  const beyond = visible < PLANETS.length
    ? `<div class="vm-row" style="--accent:var(--line-soft)" data-key="beyond"><div class="vm-row-main"><div class="vm-row-name vm-faint">${PLANETS.length - visible} more beyond telescope range</div>
       <div class="vm-row-effect">Research Telescope ${telescopes(state) + 1} in Projects.</div></div>${btn('Projects', 'openProjects', { cls: 'ghost' })}</div>`
    : '';
  return `${quickSell(v)}${hint}${card}
  <section class="vm-section" data-key="planet-list">
    <div class="vm-section-head">Planets <span class="aside">${owned}/${PLANETS.length}</span></div>
    <div class="vm-rows">${shown.map((d) => listRow(v, d.index)).join('')}${beyond}</div>
  </section>`;
}

export function renderAssignSheet(v: View, index: number): string {
  const { state } = v;
  const current = state.assignments[String(index)];
  const rows = Object.entries(state.prospectors).map(([id, level]) => {
    const def = prospectorDef(id);
    const at = Object.entries(state.assignments).find(([, pid]) => pid === id)?.[0];
    const where = at !== undefined ? ` · on ${PLANETS[Number(at)]!.name}` : '';
    const bonus = prospectorBonus(def, level) * roomMult(state, 'prospector');
    return `<div class="vm-row${id === current ? ' selected' : ''}" style="--accent:${PROSPECTOR_RARITY_COLOR[def.rarity]}" data-key="as-${id}">
      <div class="vm-row-main">
        <div class="vm-row-name" style="color:${PROSPECTOR_RARITY_COLOR[def.rarity]}">${esc(def.name)} <span class="vm-lv">Lv ${level}</span></div>
        <div class="vm-row-effect">${PROSPECTOR_ROLE_LABEL[def.role]} · ×${fmtFine(1 + bonus)} ${PLANET_STAT_LABEL[def.role]}${where}</div>
      </div>
      ${btn(id === current ? 'Assigned' : 'Assign', 'assign', { cls: id === current ? 'on' : 'green', data: { planet: index, id } })}
    </div>`;
  }).join('');
  return `<div class="vm-sheet-head"><h3>Prospector · ${esc(PLANETS[index]!.name)}</h3>${btn('✕', 'closeSheet', { cls: 'ghost' })}</div>
    <div class="vm-rows">${rows}</div>
    ${current ? `<div style="margin-top:8px">${btn('Unassign', 'assign', { cls: 'ghost wide', data: { planet: index, id: '' } })}</div>` : ''}`;
}
